import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useError } from '../../../context/ErrorContext';
import { baseApi } from '../../../api/baseClient';

export type TrendInterval = 'day' | 'week' | 'month';

// Punto agregado de tendencia (mismo shape que consumía el panel: { date, count }).
export interface VisitTrendPoint {
  date: string;
  count: number;
}

// Forma de /analytics/trends de lealify-base: series diaria de visitas.
interface TrendsResponse {
  from: string;
  to: string;
  series: Array<{ day: string; visits: number }>;
}

const fmt = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Clave de bucket por intervalo: día = fecha; semana = lunes de esa semana; mes = YYYY-MM-01.
const bucketKey = (iso: string, interval: TrendInterval): string => {
  const d = new Date(iso + 'T00:00:00');
  if (interval === 'month') return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  if (interval === 'week') {
    const dow = (d.getDay() + 6) % 7; // 0 = lunes
    d.setDate(d.getDate() - dow);
    return fmt(d);
  }
  return iso;
};

/**
 * Hook de tendencias de visitas — ahora desde lealify-base (/analytics/trends, serie
 * diaria del report_daily) en vez de Elasticsearch/esapi. Agrega la serie diaria a
 * día/semana/mes en el cliente, conservando el shape { date, count } del panel.
 */
export const useVisitTrendsData = (defaultInterval: TrendInterval = 'day') => {
  const { currentUser } = useAuth();
  const { clearErrors } = useError();

  const [aggregatedData, setAggregatedData] = useState<VisitTrendPoint[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [interval, setInterval] = useState<TrendInterval>(defaultInterval);

  const fetchTrendData = useCallback(async () => {
    if (!currentUser?.businessId) {
      setAggregatedData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    clearErrors();

    // Rango según intervalo (mismas ventanas que antes).
    const endDate = new Date();
    const startDate = new Date();
    switch (interval) {
      case 'day':
        startDate.setDate(endDate.getDate() - 14); // últimos 15 días
        break;
      case 'week':
        startDate.setDate(endDate.getDate() - 8 * 7 + 1); // últimas 8 semanas
        break;
      case 'month':
        startDate.setMonth(endDate.getMonth() - 5); // últimos 6 meses
        startDate.setDate(1);
        break;
    }
    startDate.setHours(0, 0, 0, 0);

    try {
      const r = await baseApi.get<TrendsResponse>(
        `/analytics/trends?from=${fmt(startDate)}&to=${fmt(endDate)}`
      );
      const series = r.series || [];
      // Agrega visitas por bucket del intervalo.
      const buckets = new Map<string, number>();
      for (const row of series) {
        const k = bucketKey(row.day, interval);
        buckets.set(k, (buckets.get(k) || 0) + (Number(row.visits) || 0));
      }
      const points: VisitTrendPoint[] = [...buckets.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, count]) => ({ date, count }));
      setAggregatedData(points);
    } catch (err: unknown) {
      console.error(`[useVisitTrendsData] Error cargando tendencias (interval: ${interval}):`, err);
      setError(err instanceof Error ? err.message : 'Error desconocido cargando tendencias.');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.businessId, interval, clearErrors]);

  useEffect(() => {
    if (currentUser?.businessId) {
      fetchTrendData();
    } else {
      setAggregatedData([]);
      setLoading(false);
      setError(null);
    }
  }, [currentUser?.businessId, interval, fetchTrendData]);

  const changeInterval = useCallback((newInterval: TrendInterval) => {
    if (newInterval !== interval) setInterval(newInterval);
  }, [interval]);

  const refetchTrendData = useCallback(() => {
    fetchTrendData();
  }, [fetchTrendData]);

  return { aggregatedData, loading, error, refetchTrendData, interval, changeInterval };
};
